import React from "react";
import App from "./App";
import * as msal from "@azure/msal-browser";
import { AuthContext } from "./context";

const config = {
  b2c: {},
  corp: {},
};
function initialiseMsalConfig(
  b2cScopes,
  b2cClientId,
  corpScopes,
  corpClientId
) {
  /**
   * Enter here the user flows and custom policies for your B2C application
   * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
   * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
   */
  config.b2c.policies = {
    names: {
      signIn: "B2C_1_roadview_signin",
    },
    authorities: {
      signIn: {
        authority:
          "https://mainroadsb2c.b2clogin.com/mainroadsb2c.onmicrosoft.com/B2C_1_roadview_signin",
      },
    },
    authorityDomain: "mainroadsb2c.b2clogin.com",
  };

  /**
   * Configuration object to be passed to MSAL instance on creation.
   * For a full list of MSAL.js configuration parameters, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
   * For more details on using MSAL.js with Azure AD B2C, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/working-with-b2c.md
   */

  config.b2c.msalConfig = {
    auth: {
      clientId: b2cClientId,
      authority: config.b2c.policies.authorities.signIn.authority,
      knownAuthorities: [config.b2c.policies.authorityDomain], // You must identify your tenant's domain as a known authority.
    },
    cache: {
      cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
      storeAuthStateInCookie: false, // If you wish to store cache items in cookies as well as browser cache, set this to "true".
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case msal.LogLevel.Error:
              console.error(message);
              return;
            case msal.LogLevel.Info:
              console.info(message);
              return;
            case msal.LogLevel.Verbose:
              console.debug(message);
              return;
            case msal.LogLevel.Warning:
              console.warn(message);
              return;
            default:
              return;
          }
        },
      },
    },
  };

  config.corp.msalConfig = {
    auth: {
      clientId: corpClientId,
      authority:
        "https://login.microsoftonline.com/ced71ed6-76dd-43d0-9acc-cf122b3bc423",
    },
    cache: {
      cacheLocation: "sessionStorage", // This configures where your cache will be stored
      storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
  };

  /**
   * Scopes you add here will be prompted for user consent during sign-in.
   * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
   * For more information about OIDC scopes, visit:
   * https://docs.microsoft.com/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
   */
  config.b2c.loginRequest = {
    scopes: ["openid", ...b2cScopes],
  };
  config.corp.loginRequest = {
    scopes: ["openid", "profile"],
  };

  /**
   * Scopes you add here will be used to request a token from Azure AD B2C to be used for accessing a protected resource.
   * To learn more about how to work with scopes and resources, see:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
   */
  config.b2c.tokenRequest = {
    scopes: [...b2cScopes], // e.g. ["https://fabrikamb2c.onmicrosoft.com/helloapi/demo.read"]
    forceRefresh: false, // Set this to "true" to skip a cached token and go to the server to get a new token
  };
  config.corp.tokenRequest = {
    scopes: [...corpScopes],
    forceRefresh: false,
  };

  // Create the main myMSALObj instance
  config.b2c.msalObj = new msal.PublicClientApplication(config.b2c.msalConfig);
  config.corp.msalObj = new msal.PublicClientApplication(
    config.corp.msalConfig
  );
}

async function selectAccount(setAccount) {
  if (!config.b2c.msalObj) throw new Error("auth config not yet initialised");
  /**
   * See here for more info on account retrieval:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */

  await config.b2c.msalObj.handleRedirectPromise();
  //there are circumstances where the redirect has been as a result of a corp login rather than b2c login
  //(i.e. coming from Nomad to Road View and requiring corp authentication)
  await config.corp.msalObj.handleRedirectPromise();

  const currentAccounts = config.b2c.msalObj.getAllAccounts();
  if (currentAccounts.length < 1) {
    signIn();
    return;
  } else if (currentAccounts.length > 1) {
    /**
     * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
     * is cached as a new account, which results in more than one account in the cache. Here we make
     * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
     * as this is the default flow the user initially signed-in with.
     */
    const accounts = currentAccounts.filter(
      (account) =>
        account.homeAccountId
          .toUpperCase()
          .includes(config.b2c.policies.names.signIn.toUpperCase()) &&
        account.idTokenClaims.iss
          .toUpperCase()
          .includes(config.b2c.policies.authorityDomain.toUpperCase()) &&
        account.idTokenClaims.aud === config.b2c.msalConfig.auth.clientId
    );

    if (accounts.length > 1) {
      // localAccountId identifies the entity for which the token asserts information.
      if (
        accounts.every(
          (account) => account.localAccountId === accounts[0].localAccountId
        )
      ) {
        // All accounts belong to the same user
        setAccount(accounts[0]);
      } else {
        // Multiple users detected. Logout all to be safe.
        signOut();
      }
    } else if (accounts.length === 1) {
      setAccount(accounts[0]);
    }
  } else if (currentAccounts.length === 1) {
    setAccount(currentAccounts[0]);
  }
}

function signIn() {
  if (!config.b2c.msalObj) throw new Error("auth config not yet initialised");
  /**
   * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
   */

  config.b2c.msalObj.loginRedirect(config.b2c.loginRequest);
}

function signOut() {
  if (!config.b2c.msalObj) throw new Error("auth config not yet initialised");
  /**
   * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
   */

  const logoutRequest = {
    postLogoutRedirectUri: config.b2c.msalConfig.auth.redirectUri,
  };

  config.b2c.msalObj.logoutRedirect(logoutRequest);
}

/** Gets the auth token. By default will get a token for b2cScopes in mapSettings, but can be supplied a custom scope */
export async function getAuthToken(accountId, scopes = null) {
  if (!config.b2c.msalObj) throw new Error("auth config not yet initialised");
  /**
   * See here for more info on account retrieval:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  const request = {
    ...config.b2c.tokenRequest,
    scopes: scopes ?? config.b2c.tokenRequest.scopes,
    account: config.b2c.msalObj.getAccountByHomeId(accountId),
  };

  try {
    const response = await config.b2c.msalObj.acquireTokenSilent(request);
    // In case the response from B2C server has an empty accessToken field
    // throw an error to initiate token acquisition
    if (!response.accessToken || response.accessToken === "") {
      throw new msal.InteractionRequiredAuthError();
    } else {
      console.log("b2c access_token acquired at: " + new Date().toString());
      return response.accessToken;
    }
  } catch (error) {
    console.log(
      "Silent token acquisition fails. Acquiring token using popup. \n",
      error
    );
    if (error instanceof msal.InteractionRequiredAuthError) {
      // fallback to interaction when silent call fails
      return config.b2c.msalObj.acquireTokenRedirect(request);
    } else {
      console.log(error);
    }
  }
}

export async function getCorpAuthToken(username, defaultRedirect = false) {
  if (!config.corp.msalObj) throw new Error("auth config not yet initialised");
  //issue with msal: getAccountByUsername isn't segregated by client id, so it will fetch the first account
  //from *any* msalObj with a matching username.
  const accounts = config.corp.msalObj.getAllAccounts();
  const request = {
    ...config.corp.tokenRequest,
    scopes: config.corp.tokenRequest.scopes,
    account:
      accounts.find(
        (account) =>
          account.environment === "login.windows.net" &&
          account.username === username
      ) ?? accounts.find((account) => account.username === username),
  };

  try {
    const response = await config.corp.msalObj.acquireTokenSilent(request);
    if (!response.accessToken || response.accessToken === "") {
      throw new msal.InteractionRequiredAuthError();
    } else {
      return response.accessToken;
    }
  } catch (error) {
    console.log(
      `Silent token acquisition fails. Acquiring token using ${
        defaultRedirect ? "redirect" : "popup"
      }.`
    );
    if (error instanceof msal.InteractionRequiredAuthError) {
      if (defaultRedirect) return getCorpAuthTokenRedirect(request);
      else return await getCorpAuthTokenPopup(request);
    } else {
      console.warn(error);
    }
  }
}

async function getCorpAuthTokenPopup(request) {
  // fallback to interaction when silent call fails
  try {
    const popupResponse = await config.corp.msalObj.acquireTokenPopup(request);
    console.log("corp access_token acquired at: " + new Date().toString());

    return popupResponse.accessToken === "" ? null : popupResponse.accessToken;
  } catch (popupError) {
    console.warn(popupError);
    return getCorpAuthTokenRedirect(request);
  }
}

function getCorpAuthTokenRedirect(request) {
  try {
    return config.corp.msalObj.acquireTokenRedirect(request);
  } catch (redirectError) {
    console.warn(redirectError);
  }
}

export default class Auth extends React.Component {
  constructor(props) {
    super(props);

    this.state = { authorised: false, account: null };
    this.signOut = this.signOut.bind(this);
  }

  async componentDidMount() {
    const settingsResponse = await fetch(
      `${process.env.PUBLIC_URL}/mapSettings.json`
    );
    const mapSettings = await settingsResponse.json();
    initialiseMsalConfig(
      mapSettings.b2cScopes,
      mapSettings.b2cClientId,
      mapSettings.corpScopes,
      mapSettings.corpClientId
    );
    selectAccount(this.signIn.bind(this));
  }

  signIn(account) {
    const { homeAccountId: accountId, username } = account;
    this.setState({
      authorised: true,
      account: {
        accountId,
        username,
        isCorp: account.idTokenClaims.idp != null,
      },
    });
  }

  async signOut() {
    this.setState({ authorised: false, account: null });
    signOut();
  }

  render() {
    return this.state.authorised ? (
      <AuthContext.Provider value={this.state.account}>
        <App {...this.props} signOut={this.signOut} />
      </AuthContext.Provider>
    ) : (
      <div>Authorising...</div>
    );
  }
}
